import React, { createContext, useContext, useState } from 'react';
import { User } from '../types';
import { apiServices } from '../api/services';
import { BaseApiService } from '../api/services/BaseApiService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (user: User, token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'gym_planner_user';
const TOKEN_STORAGE_KEY = 'gym_planner_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem(AUTH_STORAGE_KEY);
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const login = (user: User, token: string) => {
    console.log(' [AUTH] Logging in user with JWT token:', user.id);
    setUser(user);
    setToken(token);
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      console.log(' [AUTH] Saved user and token to localStorage');
    } catch (error) {
      console.error('Failed to save user/token to localStorage', error);
    }
  };

  // Clear all app caches on logout. exercises_cache/muscle_groups_cache/workout_templates_cache/
  // template_exercises_cache/exercise_definitions_cache used to live here too, but nothing writes
  // those keys anymore (TODO.md F2 - exercise/template caching consolidated onto apiServices.exercises'
  // own in-memory cache, cleared below via clearCache() instead).
  const ALL_CACHE_KEYS = [
    'gym_planner_user', 'gym_planner_token',
    'workout_history_cache',
    'TAB_KEY', 'VIEW_KEY', 'WORKOUT_DATE_KEY', 'last_viewed_template'
  ];

  const logout = () => {
    console.log(' [AUTH] Logging out user:', user?.id);
    setUser(null);
    setToken(null);
    try {
      // Clear auth tokens
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      // Clear all caches and UI state
      ALL_CACHE_KEYS.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Failed to clear cache ${key}:`, error);
        }
      });
      // Clear API service caches and session. apiServices.exercises.clearCache() matters here
      // specifically: its cache key is scoped by locale/muscleGroup/type/category, not by user, so
      // without this a login by a different user could see the previous user's cached private
      // exercises until the cache's own TTL expired (TODO.md F2).
      apiServices.exercises.clearCache();
      apiServices.workoutSession.clearSession();
      BaseApiService.clearAllUnauthorized();
      console.log(' [AUTH] Cleared all caches, UI state, and API session on logout');
    } catch (error) {
      console.error('Failed to clear caches/session on logout', error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
